/**
 * Pure GitHub attention mapping helpers (kept small for maintainability / CRAP).
 */

import type {
  AttentionStatus,
  CurrentUserIdentity,
  GitHubPullRequest,
} from './types';
import { isMatchingUser } from './types';

export type GitHubSignalFlags = {
  hasFails: boolean;
  allChecksPassed: boolean;
  isApproved: boolean;
  changesRequested: boolean;
};

export function githubSignalFlags(pr: GitHubPullRequest): GitHubSignalFlags {
  return {
    hasFails: pr.checkRuns.some((check) => check.conclusion === 'fail'),
    allChecksPassed:
      pr.checkRuns.length > 0 &&
      pr.checkRuns.every((check) => check.conclusion === 'ok' || check.conclusion === 'passed'),
    isApproved: pr.reviewers.some((reviewer) => reviewer.state === 'Aprobado'),
    changesRequested: pr.reviewers.some((reviewer) => reviewer.state === 'Cambios solicitados'),
  };
}

export function isPullRequestAuthor(
  pr: GitHubPullRequest,
  currentUser?: CurrentUserIdentity,
): boolean {
  if (currentUser) return isMatchingUser(pr.author, currentUser);
  if (pr.author.login === 'currentUser') return true;
  return Boolean(pr.author.name?.includes('(tú)'));
}

function statusForAuthor(flags: GitHubSignalFlags): AttentionStatus {
  if (flags.changesRequested) return 'you';
  if (flags.allChecksPassed && flags.isApproved) return 'ready';
  return 'them';
}

function statusForReviewer(state: string | undefined): AttentionStatus {
  if (state === 'Aprobado' || state === 'Cambios solicitados') return 'them';
  return 'you';
}

function reviewerMatchesCurrentUser(
  reviewer: GitHubPullRequest['reviewers'][number],
  currentUser?: CurrentUserIdentity,
): boolean {
  if (!currentUser) return Boolean(reviewer.isCurrentUser);
  if (reviewer.isCurrentUser) return true;
  return isMatchingUser(reviewer.user, currentUser);
}

function findCurrentReviewer(
  pr: GitHubPullRequest,
  currentUser?: CurrentUserIdentity,
) {
  return pr.reviewers.find((reviewer) => reviewerMatchesCurrentUser(reviewer, currentUser));
}

export function deriveGitHubAttentionStatus(
  pr: GitHubPullRequest,
  currentUser?: CurrentUserIdentity,
): AttentionStatus {
  if (pr.zuneloMeta?.status) return pr.zuneloMeta.status;

  const flags = githubSignalFlags(pr);
  if (pr.merged) return flags.hasFails ? 'blocked' : 'them';
  if (flags.hasFails) return 'blocked';

  if (isPullRequestAuthor(pr, currentUser)) {
    return statusForAuthor(flags);
  }

  const currentReviewer = findCurrentReviewer(pr, currentUser);
  if (currentReviewer) return statusForReviewer(currentReviewer.state);

  if (flags.allChecksPassed && flags.isApproved) return 'ready';
  return 'them';
}

export type MergeCopy = { mergeTitle: string; mergeText: string; mergeCta: string };

const DEFAULT_MERGE_COPY: MergeCopy = {
  mergeTitle: 'En revisión',
  mergeText: 'Esperando validaciones.',
  mergeCta: 'Revisar PR',
};

function mergedCopy(hasFails: boolean): MergeCopy {
  if (hasFails) {
    return {
      mergeTitle: 'Mergeado · deploy en rojo',
      mergeText: 'El job de producción falló y revirtió.',
      mergeCta: 'Ver logs del deploy',
    };
  }
  return {
    mergeTitle: 'Mergeado',
    mergeText: 'Merge completado con éxito.',
    mergeCta: 'Ver logs del deploy',
  };
}

export function buildMergeCopy(pr: GitHubPullRequest, flags: GitHubSignalFlags): MergeCopy {
  if (pr.merged) return mergedCopy(flags.hasFails);
  if (flags.changesRequested) {
    return {
      mergeTitle: 'Bloqueado por revisión',
      mergeText: 'Se solicitaron cambios antes de poder mergear.',
      mergeCta: 'Responder revisión',
    };
  }
  if (flags.hasFails) {
    return {
      mergeTitle: 'CI en rojo',
      mergeText: 'Hay checks fallando en el pipeline.',
      mergeCta: 'Ver logs del check',
    };
  }
  if (flags.allChecksPassed && flags.isApproved) {
    return {
      mergeTitle: 'Listo para mergear',
      mergeText: 'CI en verde, aprobado y sin conflictos con main.',
      mergeCta: `Merge y cerrar ${pr.linkedTicketKey || pr.title}`,
    };
  }
  return { ...DEFAULT_MERGE_COPY };
}

export function githubKindLabel(pr: GitHubPullRequest, changesRequested: boolean): string {
  if (pr.merged) return 'Mergeado';
  if (changesRequested) return 'Con conflictos';
  return 'Listo para revisión';
}
